/**
 * Lead Detail Page
 *
 * Complete detail view for a single lead record with tabs
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { persistedLeadRecords } from '../../services/mockData/leads';
import { getRecordById, deleteRecord } from '../../services/recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { DetailPageLayout } from '../../components/detail/DetailPageLayout';
import { DetailHeader } from '../../components/detail/DetailHeader';
import { DetailTabs } from '../../components/detail/DetailTabs';
import { LayoutSection } from '../../components/detail/LayoutSection';
import { ActivityTab } from '../../components/detail/ActivityTab';
import { LeadModal } from '../../components/modals/LeadModal';
import { LogCallModal } from '../../components/modals/LogCallModal';
import { LogMeetingModal } from '../../components/modals/LogMeetingModal';
import { LogTaskModal } from '../../components/modals/LogTaskModal';
import { AddNoteModal } from '../../components/modals/AddNoteModal';
import { SendEmailModal } from '../../components/modals/SendEmailModal';
import { Mail } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { usePageLayout, usePageLayoutTabs } from '../../hooks/useObjectManager';

interface LeadDetailPageProps {
  id: string;
  onBack: () => void;
}

export const LeadDetailPage: React.FC<LeadDetailPageProps> = ({ id, onBack }) => {
  const { success, info } = useToast();
  const allTabs = usePageLayoutTabs('Lead__c');
  // PE-1458: Remove Notes tab - notes consolidated into Activity Timeline
  const tabs = allTabs.filter((t: { id: string }) => t.id !== 'notes');
  const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? 'overview');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const queryClient = useQueryClient();

  const { data: lead, isLoading } = useQuery({
    queryKey: ['lead', id],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        const record = await getRecordById('Lead', id);
        if (!record) throw new Error('Lead not found');
        return { ...record, name: record.name ?? record.label ?? (record as any).Name__c ?? record.id };
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const allLeads = persistedLeadRecords.get();
      const found = allLeads.find(l => l.id === id);
      if (!found) throw new Error('Lead not found');
      return found;
    }
  });

  // Fetch page layout (uses Record Pages assignment system)
  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Lead__c', lead);

  const handleEdit = () => {
    setIsModalOpen(true);
  };

  const handleDelete = () => {
    const displayName = lead?.name ?? (lead as any)?.Name__c ?? lead?.label ?? id;
    if (!window.confirm(`Are you sure you want to delete ${displayName}?`)) return;
    if (shouldUseGobodo()) {
      deleteRecord('Lead', id)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['leadListViewData'] });
          queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Lead__c'] });
          success('Lead deleted', `${displayName} has been deleted`);
          onBack();
        })
        .catch(() => success('Error', 'Failed to delete lead'));
      return;
    }
    const allLeads = persistedLeadRecords.get();
    persistedLeadRecords.set(allLeads.filter(l => l.id !== id));
    success('Lead deleted', `${displayName} has been deleted`);
    onBack();
  };

  const handleConvert = () => {
    info('Coming Soon', 'Convert lead functionality will be available soon. This will create a Client, Contact, and optionally an Opportunity.');
  };

  if (isLoading) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Leads">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DetailPageLayout>
    );
  }

  if (!lead) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Leads">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Lead not found</p>
        </div>
      </DetailPageLayout>
    );
  }

  // Status badge colors
  const statusColors: Record<string, string> = {
    New: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    Contacted: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    Qualified: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    Unqualified: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
  };

  const isOverview = ['overview', 'tab_001', 'details'].includes(activeTab);
  const isActivity = ['activity', 'tab_003'].includes(activeTab);

  return (
    <DetailPageLayout onBack={onBack} backLabel="Back to Leads">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <DetailHeader
          title={(lead.name ?? (lead as any).Name__c ?? lead.label) as string}
          subtitle={`${(lead.title ?? (lead as any).Title__c) || 'Lead'} at ${lead.company ?? (lead as any).Company__c ?? ''}`}
          recordId={lead.id as string}
          status={(lead.status ?? (lead as any).Status__c) as string}
          statusColor={statusColors[(lead.status ?? (lead as any).Status__c) as string]}
          onEdit={handleEdit}
          onDelete={handleDelete}
          additionalActions={
            <button
              onClick={() => setShowEmailModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Mail className="w-4 h-4" />
              Send Email
            </button>
          }
        />

        {/* Convert Button (special for Leads) */}
        {(lead.status ?? (lead as any).Status__c) === 'Qualified' && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Ready to convert?
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  This qualified lead can be converted to a Client and Contact.
                </p>
              </div>
              <button
                onClick={handleConvert}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Convert Lead
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <DetailTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          <div className="p-6">
            {isOverview && (
              <div className="space-y-6">
                {loadingLayout ? (
                  // Loading state
                  <div className="space-y-6">
                    {[1, 2, 3].map(i => (
                      <div
                        key={i}
                        className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6"
                      >
                        <div className="animate-pulse space-y-3">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Render sections from page layout
                  pageLayout?.sections.map(section => (
                    <LayoutSection
                      key={section.id}
                      section={section}
                      objectApiName="Lead"
                      record={lead}
                    />
                  ))
                )}
              </div>
            )}

            {isActivity && (
              <ActivityTab
                recordId={lead.id as string}
                recordType="lead"
                onLogActivity={(type) => {
                  if (type === 'call') setShowCallModal(true);
                  if (type === 'meeting') setShowMeetingModal(true);
                  if (type === 'task') setShowTaskModal(true);
                  if (type === 'note') setShowNoteModal(true);
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Lead Edit Modal */}
      <LeadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        lead={lead}
      />

      {/* Log Call Modal */}
      <LogCallModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        recordId={lead.id as string}
        recordType="lead"
        contactName={(lead.name ?? (lead as any).Name__c ?? lead.label) as string}
      />

      {/* Log Meeting Modal */}
      <LogMeetingModal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        recordId={lead.id as string}
        recordType="lead"
      />

      {/* Log Task Modal */}
      <LogTaskModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        recordId={lead.id as string}
        recordType="lead"
        contactName={(lead.name ?? (lead as any).Name__c ?? lead.label) as string}
      />

      {/* Add Note Modal */}
      <AddNoteModal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        recordId={lead.id as string}
        recordType="lead"
      />

      {/* Send Email Modal */}
      <SendEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        recordId={lead.id as string}
        recordType="lead"
        recipientEmail={(lead.email ?? (lead as any).Email__c) as string}
        recipientName={(lead.name ?? (lead as any).Name__c ?? lead.label) as string}
      />
    </DetailPageLayout>
  );
};
